import React from 'react';

import { HOVER_GLOW_EDGE_SHRINK } from '../theme/tokens';

/**
 * Drives the cursor-following hover glow shared by every clickable card (see
 * `theme/mixins.hoverWash`). Mounted ONCE by the layout; it listens for pointer
 * movement at the document level and writes the pointer's position, in the card's
 * local coordinates, into the `--ows-glow-x/-y` custom properties on whichever
 * glow card the cursor is over. The card's `::after` reads those to place its
 * radial highlight, so the glow tracks the cursor with zero per-card wiring.
 *
 * Cards are identified by the `--ows-glow-card` marker the mixin sets. It's
 * registered (below) as a NON-inheriting typed property, so only the card root
 * reports `1` and its descendants report `0` — that's what lets us resolve the
 * exact card element from a deeply-nested event target. `--ows-glow-x/-y` are
 * registered as inheriting `<length-percentage>`s so the highlight can be
 * transitioned (a smooth glide toward the pointer) and so the pseudo inherits them
 * from the card. Browsers without `CSS.registerProperty` simply skip the
 * controller and fall back to the mixin's centred 50% glow.
 */

const MARKER = '--ows-glow-card';
const VAR_X = '--ows-glow-x';
const VAR_Y = '--ows-glow-y';
const VAR_SCALE = '--ows-glow-scale';
// Two <angle>s that drive the border sweep's counter-rotating conic gradients (see
// mixins.hoverWash). Registered here (the single home for the glow custom props) so
// they're typed as <angle> and therefore *interpolate* under the CSS keyframes —
// the whole reason the sweep can rotate. Animated purely in CSS; no JS writes them.
const VAR_SWEEP_A = '--ows-sweep-a';
const VAR_SWEEP_B = '--ows-sweep-b';

/** Cap the walk up from the event target to the card (defends against deep trees). */
const MAX_DEPTH = 16;

let propsReady: boolean | null = null;

/** Register the typed custom properties once. Returns whether the spotlight can run. */
const ensureProps = (): boolean => {
  if (propsReady !== null) return propsReady;
  const css = window.CSS as typeof window.CSS & {
    registerProperty?: (d: {
      name: string;
      syntax: string;
      inherits: boolean;
      initialValue: string;
    }) => void;
  };
  if (typeof css?.registerProperty !== 'function') {
    propsReady = false;
    return false;
  }
  // Register each property INDEPENDENTLY. A property that's already registered (a
  // remount / HMR, or one carried over from an earlier build) throws — and a single
  // shared try/catch would let that first throw skip every registration after it.
  // That's exactly how the sweep <angle>s stayed unregistered, so their keyframes
  // animated in discrete, choppy jumps instead of interpolating. Per-prop try/catch
  // makes each one independent, so a new prop registers even when the old ones exist.
  const defs = [
    { name: MARKER, syntax: '<integer>', inherits: false, initialValue: '0' },
    { name: VAR_X, syntax: '<length-percentage>', inherits: true, initialValue: '50%' },
    { name: VAR_Y, syntax: '<length-percentage>', inherits: true, initialValue: '50%' },
    { name: VAR_SCALE, syntax: '<number>', inherits: true, initialValue: '1' },
    { name: VAR_SWEEP_A, syntax: '<angle>', inherits: false, initialValue: '0deg' },
    { name: VAR_SWEEP_B, syntax: '<angle>', inherits: false, initialValue: '0deg' },
  ];
  for (const def of defs) {
    try {
      // Call with the CSS receiver — detaching it throws "Illegal invocation".
      css.registerProperty(def);
    } catch {
      // Already registered — registered properties persist for the document lifetime.
    }
  }
  propsReady = true;
  return true;
};

/** Walk up from the event target to the nearest element carrying the glow marker. */
const findCard = (start: EventTarget | null): HTMLElement | null => {
  let el = start instanceof Element ? start : null;
  let depth = 0;
  while (el && depth < MAX_DEPTH) {
    if (el instanceof HTMLElement && getComputedStyle(el).getPropertyValue(MARKER).trim() === '1') {
      return el;
    }
    el = el.parentElement;
    depth += 1;
  }
  return null;
};

export const usePointerGlow = (): void => {
  React.useEffect(() => {
    if (!ensureProps()) return undefined;

    // Resolved card + its cached rect for the current pointer region. Re-resolving
    // the card (a getComputedStyle walk) only happens when the pointer crosses into
    // a new DOM element, so the steady-state hot path is two style writes per frame.
    let lastTarget: EventTarget | null = null;
    let card: HTMLElement | null = null;
    let rect: DOMRect | null = null;

    // Coalesce the (possibly many) pointer events per frame into one write.
    let frame = 0;
    let pending: PointerEvent | null = null;

    const flush = (): void => {
      frame = 0;
      const e = pending;
      pending = null;
      if (!e) return;

      if (e.target !== lastTarget) {
        lastTarget = e.target;
        const next = findCard(e.target);
        if (next !== card) {
          card = next;
          rect = card ? card.getBoundingClientRect() : null;
        }
      }
      if (!card || !rect) return;

      // The cached rect can go stale if the card scrolled under a still pointer
      // target; if the pointer reads as outside it, re-measure before writing.
      if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
        rect = card.getBoundingClientRect();
      }
      const lx = e.clientX - rect.left;
      const ly = e.clientY - rect.top;
      // Edge-shrink: how far the pointer is from the card's centre on its nearest
      // axis, 0 (centre) → 1 (any border). Chebyshev (max of the two axes) so the
      // pool reaches its smallest at every edge, not only the corners.
      const edge = Math.min(
        1,
        Math.max(Math.abs((lx / rect.width) * 2 - 1), Math.abs((ly / rect.height) * 2 - 1)),
      );
      card.style.setProperty(VAR_X, `${Math.round(lx)}px`);
      card.style.setProperty(VAR_Y, `${Math.round(ly)}px`);
      card.style.setProperty(VAR_SCALE, (1 - HOVER_GLOW_EDGE_SHRINK * edge).toFixed(3));
    };

    const onMove = (e: PointerEvent): void => {
      pending = e;
      if (!frame) frame = requestAnimationFrame(flush);
    };

    document.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      document.removeEventListener('pointermove', onMove);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
};

export default usePointerGlow;
